import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xxtract/ui';
import { useMappingProfileVersions, useResetMappingProfile } from '../hooks/useIntake';
import { MappingProfileVersionList } from '../components/MappingProfileVersionList';
import { MappingProfileResetDialog } from '../components/MappingProfileResetDialog';

/**
 * h5-7 (FR35): Beheer-only screen at /admin/mapping-profiles. An employee looks up a customer by
 * GLN (there is no reusable customer-picker component in the portal yet — a direct GLN lookup is
 * the minimal, defensible scope for this story) to inspect their mapping profile's full version
 * history and, if needed, reset it back to empty with a mandatory audit note.
 */
export function MappingProfileAdminPage() {
  const { t } = useTranslation();
  const [glnInput, setGlnInput] = useState('');
  const [activeGln, setActiveGln] = useState<string | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const glnFieldId = useId();

  const { data: versions, isLoading } = useMappingProfileVersions(activeGln);
  const resetProfile = useResetMappingProfile(activeGln);

  function handleLookup() {
    const trimmed = glnInput.trim();
    if (trimmed === '') return;
    setActiveGln(trimmed);
  }

  function handleReset(note: string) {
    resetProfile.mutate(note, { onSuccess: () => setResetDialogOpen(false) });
  }

  return (
    <section data-testid="mapping-profile-admin-page" className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-bold text-[var(--foreground-heading)]">{t('admin.mappingProfiles.title')}</h1>
        <p className="mt-1 text-sm text-[var(--foreground-muted)]">{t('admin.mappingProfiles.description')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('admin.mappingProfiles.lookup.title')}</CardTitle>
          <CardDescription>{t('admin.mappingProfiles.lookup.description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--foreground)]" htmlFor={glnFieldId}>
              {t('admin.mappingProfiles.lookup.glnLabel')}
            </label>
            <input
              id={glnFieldId}
              type="text"
              data-testid="mapping-profile-gln-input"
              value={glnInput}
              onChange={(event) => setGlnInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleLookup();
              }}
              className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
            />
          </div>
          <Button data-testid="mapping-profile-lookup-cta" onClick={handleLookup}>
            {t('admin.mappingProfiles.lookup.cta')}
          </Button>
        </CardContent>
      </Card>

      {activeGln ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.mappingProfiles.history.title', { gln: activeGln })}</CardTitle>
            <div>
              <Button variant="destructive" data-testid="mapping-profile-reset-cta" onClick={() => setResetDialogOpen(true)}>
                {t('admin.mappingProfiles.resetCta')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p role="status">{t('common.loading')}</p>
            ) : (
              <MappingProfileVersionList versions={versions ?? []} />
            )}
          </CardContent>
        </Card>
      ) : null}

      <MappingProfileResetDialog
        open={resetDialogOpen}
        onOpenChange={setResetDialogOpen}
        isResetting={resetProfile.isPending}
        onConfirm={handleReset}
      />
    </section>
  );
}
